import React, { useState } from 'react';
import { useCSVStore, ColumnConflict, UploadedFile } from '../../stores/csvStore';
import './ColumnConflictResolver.css';

interface ColumnConflictResolverProps {
  isOpen: boolean;
  onClose: () => void;
  onResolveAll: (resolutions: { [columnName: string]: string }) => void;
}

const ColumnConflictResolver: React.FC<ColumnConflictResolverProps> = ({
  isOpen,
  onClose,
  onResolveAll
}) => {
  const { columnConflicts, uploadedFiles } = useCSVStore();
  const [resolutions, setResolutions] = useState<{ [columnName: string]: string }>({});

  if (!isOpen || columnConflicts.length === 0) {
    return null;
  }

  const handleFileSelection = (columnName: string, fileId: string) => {
    setResolutions(prev => ({
      ...prev,
      [columnName]: fileId
    }));
  };

  const handleResolveAll = () => {
    // Check if all conflicts are resolved
    const unresolvedConflicts = columnConflicts.filter(
      conflict => !resolutions[conflict.columnName]
    );

    if (unresolvedConflicts.length > 0) {
      alert(`Please resolve all conflicts. Missing selections for: ${unresolvedConflicts.map(c => c.columnName).join(', ')}`);
      return;
    }

    console.log('🔧 Resolving conflicts with:', resolutions);
    onResolveAll(resolutions);
    onClose();
  };

  const getFileName = (fileId: string): string => {
    const file = uploadedFiles.find(f => f.id === fileId);
    return file ? file.name : 'Unknown File';
  };

  return (
    <div className="modal-overlay">
      <div className="conflict-resolver-modal">
        <div className="modal-header">
          <h2>🔧 Resolve Column Conflicts</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-content">
          <p className="conflict-explanation">
            Some columns exist in multiple files. Please choose which file to use for each conflicted column.
          </p>

          <div className="conflicts-list">
            {columnConflicts.map(conflict => (
              <div key={conflict.columnName} className="conflict-item">
                <div className="conflict-header">
                  <h3>Column: "{conflict.columnName}"</h3>
                  <span className="conflict-count">
                    Found in {conflict.files.length} files
                  </span>
                </div>

                <div className="file-options">
                  {conflict.files.map(fileInfo => (
                    <label key={fileInfo.fileId} className="file-option">
                      <input
                        type="radio"
                        name={`conflict-${conflict.columnName}`}
                        value={fileInfo.fileId}
                        checked={resolutions[conflict.columnName] === fileInfo.fileId}
                        onChange={() => handleFileSelection(conflict.columnName, fileInfo.fileId)}
                      />
                      <div className="file-option-content">
                        <span className="file-name">{fileInfo.fileName}</span>
                        <span className="file-preview">
                          {/* Show a preview of the column data */}
                          Preview: {getColumnPreview(fileInfo.fileId, conflict.columnName)}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>

                {!resolutions[conflict.columnName] && (
                  <div className="conflict-warning">
                    ⚠️ Please select which file to use for this column
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="resolve-btn"
            onClick={handleResolveAll}
            disabled={Object.keys(resolutions).length !== columnConflicts.length}
          >
            Resolve All Conflicts ({Object.keys(resolutions).length}/{columnConflicts.length})
          </button>
        </div>
      </div>
    </div>
  );

  function getColumnPreview(fileId: string, columnName: string): string {
    const file = uploadedFiles.find(f => f.id === fileId);
    if (!file) return 'No preview';

    const columnIndex = file.parsedData.headers.findIndex(h => 
      h.trim().toLowerCase() === columnName.trim().toLowerCase()
    );
    
    if (columnIndex === -1) return 'Column not found';

    // Get first few non-empty values
    const values = file.parsedData.rows
      .map(row => row[columnIndex])
      .filter(val => val && val.toString().trim())
      .slice(0, 3);

    return values.length > 0 ? values.join(', ') + '...' : 'No data';
  }
};

export default ColumnConflictResolver;
